import { forwardRef } from 'react';

import { cva, type VariantProps } from 'cva';

import styles from './sheet.module.css';

export const sheetVariants = cva({
  base: null,
  variants: {
    variant: {
      stroke: styles.stroke,
      fill: styles.fill,
      fill_2: styles.fill_2,
      banner: styles.banner,
      ghost: null,
    },
    spacing: {
      s: styles.spacing_s,
      m: styles.spacing_m,
      l: styles.spacing_l,
      xl: styles.spacing_xl,
      xs: styles.spacing_xs,
      '2xs': styles.spacing_2xs,
      '3xs': styles.spacing_3xs,
    },
  },
  defaultVariants: {
    variant: 'fill',
    spacing: 'm',
  },
});

export type SheetProps = React.HTMLAttributes<HTMLDivElement> &
  VariantProps<typeof sheetVariants>;

/**
 * Sheet is a simple container component that provides a background and spacing
 * around its children.
 *
 * Use variant="stroke" for a border or variant="ghost" for no background.
 *
 * You can use the `sheetVariants` helper to create a custom component with the
 * the props and styling from Sheet.
 *
 * ```tsx
 * <Sheet variant="stroke" spacing="m">
 *  Hello, world!
 * </Sheet>
 * ```
 */
export const Sheet = forwardRef<HTMLDivElement, SheetProps>(
  ({ variant, spacing, className, ...props }, ref) => {
    const classes = sheetVariants({ variant, spacing, className });

    return <div ref={ref} {...props} className={classes} />;
  },
);
